<template>
  <div class="bill-journal-entry-container">
    <div class="bill-journal-entry-header">
      <!-- Breadcrumb removed -->
    </div>
    <div class="bill-journal-entry-content-transparent">
      <div class="bill-journal-entry-header-row">
        <h2 class="bill-journal-entry-title">
          Bill Journal Entry
          <button class="bill-journal-entry-help-btn" @click="showHelpModal" title="Help">
            <span class="bill-journal-entry-help-icon">?</span>
          </button>
        </h2>
        <button v-if="isModal" class="bill-journal-entry-close-btn" @click="cancel" title="Close">
          <span class="bill-journal-entry-close-icon">&times;</span>
        </button>
      </div>
      <div class="bill-journal-entry-meta">
        <div class="bill-journal-entry-meta-field">
          <label>Date</label>
          <input type="text" class="bill-journal-entry-input-date" v-model="dateUS" placeholder="MM/DD/YYYY" />
        </div>
        <div class="bill-journal-entry-meta-field">
          <label>No*</label>
          <input type="text" class="bill-journal-entry-input-no" v-model="entryNo" readonly :placeholder="isLoadingEntryNo ? 'Generating...' : 'Auto-generated'" />
        </div>
      </div>
      <div class="bill-journal-entry-table-section">
        <div class="bill-journal-entry-table-title">Entries</div>
        <div class="bill-journal-entry-validation" v-if="validationError">
          <span class="bill-journal-entry-validation-error">{{ validationError }}</span>
        </div>
        <table class="bill-journal-entry-table">
          <thead>
            <tr>
              <th>Account</th>
              <th>Memo</th>
              <th>Debit</th>
              <th>Credit</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="rows.length === 0">
              <td colspan="5" class="bill-journal-entry-no-records">No records to show</td>
            </tr>
            <tr v-for="(row, idx) in rows" :key="idx">
              <td>
                <select v-model="row.account" class="bill-journal-entry-input-table" @change="validateEntries">
                  <option value="">Select account</option>
                  <option v-for="account in getFilteredAccountsForRow(idx)" :key="account.id" :value="account.id">
                    {{ account.accountName }} ({{ account.accountNumber }}) - {{ account.accountType }}
                  </option>
                </select>
              </td>
              <td><input type="text" v-model="row.memo" placeholder="Memo" class="bill-journal-entry-input-table" /></td>
              <td><input type="number" v-model.number="row.debit" min="0" step="0.01" placeholder="0.00" class="bill-journal-entry-input-table" @input="handleDebitInput(row, $event.target.value)" /></td>
              <td><input type="number" v-model.number="row.credit" min="0" step="0.01" placeholder="0.00" class="bill-journal-entry-input-table" @input="handleCreditInput(row, $event.target.value)" /></td>
              <td>
                <button class="bill-journal-entry-btn-delete" @click="deleteRow(idx)" title="Delete Row" :disabled="rows.length <= 2">Delete</button>
              </td>
            </tr>
          </tbody>
        </table>
        <button class="bill-journal-entry-btn-add" @click="addRow">Add Row</button>
      </div>
      <div class="bill-journal-entry-comments">
        <label>Write comments for this Bill Journal Entry.</label>
        <textarea class="bill-journal-entry-input-comments" v-model="comments" rows="3"></textarea>
      </div>
      <div class="bill-journal-entry-totals">
        <span>Debit Total ($) : <b>{{ debitTotal.toFixed(2) }}</b></span>
        <span>Credit Total ($) : <b>{{ creditTotal.toFixed(2) }}</b></span>
        <span v-if="!isBalanced" class="bill-journal-entry-balance-error">⚠️ Totals must be equal</span>
        <span v-else class="bill-journal-entry-balance-success">✓ Balanced</span>
      </div>
      <div class="bill-journal-entry-actions">
        <button v-if="!isModal" class="bill-journal-entry-btn-action" @click="saveAndClose" :disabled="!canSave">Save and Close</button>
        <button v-if="!isModal" class="bill-journal-entry-btn-action" @click="saveAndNew" :disabled="!canSave">Save and New</button>
        <button v-if="isModal" class="bill-journal-entry-btn-action" @click="createJournalEntry" :disabled="!canSave">Create</button>
        <button class="bill-journal-entry-btn-action" @click="cancel">{{ isModal ? 'Close' : 'Cancel' }}</button>
        <button v-if="!isModal" class="bill-journal-entry-btn-action" @click="makeRecurring" :disabled="!canSave">Make it recurring</button>
      </div>
    </div>

    <!-- Confirm when consultant expense account differs from Consulting Expense -->
    <WarningModal
      :show="showExpenseAccountWarning"
      title="Expense Account Mapping"
      :message="expenseAccountWarningMessage"
      buttonText="No"
      confirmButtonText="Yes"
      :overlay-z-index="12000"
      @close="declineMappedExpenseAccount"
      @confirm="confirmMappedExpenseAccount"
    />

    <!-- Help Modal -->
    <div v-if="showHelp" class="bill-journal-entry-help-modal-overlay" @click="hideHelpModal">
      <div class="bill-journal-entry-help-modal" @click.stop>
        <div class="bill-journal-entry-help-modal-header">
          <h3>Bill Journal Entry Help</h3>
          <button class="bill-journal-entry-help-modal-close" @click="hideHelpModal">&times;</button>
        </div>
        <div class="bill-journal-entry-help-modal-content">
          <h4>What is a Bill Journal Entry?</h4>
          <p>A Bill Journal Entry records vendor bills in your accounting system. It follows the double-entry bookkeeping system where expenses are debited and accounts payable (liability) is credited.</p>
          
          <h4>Double-Entry Bookkeeping Principle</h4>
          <p><strong>For every transaction: Debits = Credits</strong></p>
          <p>This ensures the accounting equation (Assets = Liabilities + Equity) remains balanced.</p>
          
          <h4>Understanding Bill Journal Entries</h4>
          <div class="bill-journal-entry-help-account-types">
            <div class="bill-journal-entry-help-section">
              <h5>Debit (Record the Expense)</h5>
              <ul>
                <li><strong>Expense</strong> - Operating costs, utilities, supplies</li>
                <li><strong>Other Expense</strong> - Miscellaneous costs</li>
                <li><strong>Cost of Goods Sold</strong> - Direct costs of inventory</li>
                <li><strong>Payroll Liability</strong> - Employee-related expenses</li>
              </ul>
            </div>
            
            <div class="bill-journal-entry-help-section">
              <h5>Credit (Record the Liability)</h5>
              <ul>
                <li><strong>Accounts Payable</strong> - Amount owed to vendor</li>
                <li><strong>Current Liability</strong> - Short-term obligations</li>
              </ul>
            </div>
          </div>
          
          <h4>Common Bill Journal Entry Examples</h4>
          <div class="bill-journal-entry-help-section">
            <h5>Example 1: Office Supplies Bill</h5>
            <ul>
              <li><strong>Debit</strong>: Office Supplies Expense - $500</li>
              <li><strong>Credit</strong>: Accounts Payable - $500</li>
              <li><strong>Effect</strong>: Expense increases, Liability (amount owed) increases</li>
            </ul>
          </div>
          
          <div class="bill-journal-entry-help-section">
            <h5>Example 2: Utility Bill</h5>
            <ul>
              <li><strong>Debit</strong>: Utilities Expense - $300</li>
              <li><strong>Credit</strong>: Accounts Payable - $300</li>
              <li><strong>Effect</strong>: Utility expense increases, Amount owed increases</li>
            </ul>
          </div>
          
          <div class="bill-journal-entry-help-section">
            <h5>Example 3: Consultant Service Bill</h5>
            <ul>
              <li><strong>Debit</strong>: Consulting Expense - $2,000</li>
              <li><strong>Credit</strong>: Accounts Payable - $2,000</li>
              <li><strong>Effect</strong>: Consulting expense increases, Liability increases</li>
            </ul>
          </div>
          
          <h4>Important: Vendor Bills vs Customer Invoices</h4>
          <div class="bill-journal-entry-help-section">
            <table style="width: 100%; border-collapse: collapse; margin: 15px 0;">
              <thead>
                <tr style="background: #e3f2fd;">
                  <th style="padding: 10px; text-align: left;">Type</th>
                  <th style="padding: 10px; text-align: left;">Debit</th>
                  <th style="padding: 10px; text-align: left;">Credit</th>
                </tr>
              </thead>
              <tbody>
                <tr style="background: #fff;">
                  <td style="padding: 10px; border: 1px solid #ddd;"><strong>Vendor Bill</strong></td>
                  <td style="padding: 10px; border: 1px solid #ddd;">Expense Account</td>
                  <td style="padding: 10px; border: 1px solid #ddd;">Accounts Payable</td>
                </tr>
                <tr style="background: #f8f9fa;">
                  <td style="padding: 10px; border: 1px solid #ddd;"><strong>Customer Invoice</strong></td>
                  <td style="padding: 10px; border: 1px solid #ddd;">Accounts Receivable</td>
                  <td style="padding: 10px; border: 1px solid #ddd;">Income/Revenue</td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <h4>Validation Rules</h4>
          <ul>
            <li><strong>Balance Check</strong>: Debit total must equal credit total</li>
            <li><strong>Account Required</strong>: All lines must have an account selected</li>
            <li><strong>Amount Required</strong>: Each line must have either debit or credit amount</li>
            <li><strong>No Double Entry</strong>: Cannot have both debit and credit on same line</li>
            <li><strong>Minimum Entries</strong>: At least two lines required</li>
          </ul>
          
          <h4>Recommended Accounts for Vendor Bills</h4>
          <div class="bill-journal-entry-help-account-types">
            <div class="bill-journal-entry-help-section">
              <h5>Expense Accounts (Debit)</h5>
              <ul>
                <li>Office Supplies Expense</li>
                <li>Utilities Expense</li>
                <li>Rent Expense</li>
                <li>Consulting Expense</li>
                <li>Cost of Goods Sold</li>
                <li>Payroll Expense</li>
              </ul>
            </div>
            
            <div class="bill-journal-entry-help-section">
              <h5>Liability Accounts (Credit)</h5>
              <ul>
                <li>Accounts Payable</li>
                <li>Bills Payable</li>
                <li>Vendor Payables</li>
              </ul>
            </div>
          </div>
          
          <h4>Tips for Bill Journal Entries</h4>
          <ul>
            <li><strong>Always debit expense first</strong>: Record what you purchased or received</li>
            <li><strong>Credit accounts payable</strong>: Record the liability to the vendor</li>
            <li><strong>Use descriptive memos</strong>: Include vendor name and bill number</li>
            <li><strong>Match bill amounts</strong>: Ensure journal entry matches the vendor bill</li>
            <li><strong>Review before saving</strong>: Double-check debits equal credits</li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import '../styles/BillJournalEntry.css';
import ApiService from '@/services/api.service.js';
import logger from '@/utils/logger';
import JournalEntryService from '@/services/journalEntry.service.js';
import WarningModal from '@/components/WarningModal.vue';

export default {
  name: 'BillJournalEntry',
  components: {
    WarningModal
  },
  props: {
    // Whether to show as modal or full page
    isModal: {
      type: Boolean,
      default: false
    },
    // Bill data for reference (optional)
    billData: {
      type: Object,
      default: () => ({})
    },
    // When true, rows are filled from billData (when present)
    autoPopulate: {
      type: Boolean,
      default: true
    },
    // When true with isModal + autoPopulate: POST journal immediately after load (no review step)
    autoCreate: {
      type: Boolean,
      default: false
    }
  },
  emits: ['saved', 'cancelled', 'auto-create-skipped', 'auto-create-failed'],
  data() {
    // Set current date in ISO format (YYYY-MM-DD) as default
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    
    return {
      date: `${yyyy}-${mm}-${dd}`, // Store in ISO format internally
      entryNo: '',
      comments: '',
      rows: [
        { account: '', memo: '', debit: '', credit: '' },
        { account: '', memo: '', debit: '', credit: '' }
      ],
      accounts: [],
      validationError: '',
      isBalanced: false,
      isLoadingEntryNo: false,
      isLoading: false,
      error: null,
      showHelp: false,
      showExpenseAccountWarning: false,
      expenseAccountWarningMessage: '',
      // null = undecided; true = use consultant mapped account(s); false = keep Consulting Expense
      useConsultantExpenseAccount: null,
      expenseAccountChoiceResolved: false,
      pendingAutoCreateAfterExpenseChoice: false
    };
  },
  computed: {
    // Convert date to/from US format (MM/DD/YYYY)
    dateUS: {
      get() {
        return this.formatIsoToUs(this.date);
      },
      set(value) {
        this.date = this.parseUsToIso(value);
      }
    },
    debitTotal() {
      return this.rows.reduce((sum, row) => sum + (parseFloat(row.debit) || 0), 0);
    },
    creditTotal() {
      return this.rows.reduce((sum, row) => sum + (parseFloat(row.credit) || 0), 0);
    },
    canSave() {
      return this.rows.length >= 2 && 
             this.isBalanced && 
             this.date &&
             this.rows.every(row => row.account) &&
             !this.validationError;
    },
    hasChanges() {
      return this.rows.some(row => 
        row.account || row.memo || row.debit || row.credit
      ) || this.comments.trim() !== '';
    },
    companyId() {
      const storeCompany = this.$store.state.companies?.currentCompany;
      if (storeCompany && storeCompany.id) {
        return storeCompany.id;
      }
      
      const globalCompany = this.$store.state.currentCompany;
      if (globalCompany && globalCompany.id) {
        return globalCompany.id;
      }
      
      const routeCompanyId = this.$route?.params?.companyId || this.$route?.params?.id;
      if (routeCompanyId) {
        return routeCompanyId;
      }
      
      const localCompanyId = localStorage.getItem('currentCompanyId');
      if (localCompanyId) {
        return localCompanyId;
      }
      
      return null;
    }
  },
  methods: {
    // Filter accounts based on row - expense rows show expense accounts, last row shows liability accounts
    getFilteredAccountsForRow(rowIndex) {
      // Determine if this is the last row (Accounts Payable/Credit row)
      const isLastRow = rowIndex === this.rows.length - 1;
      
      // Check if this row has a credit value (indicates it's a liability/payable row)
      const hasCredit = this.rows[rowIndex] && parseFloat(this.rows[rowIndex].credit) > 0;
      
      // Last row or row with credit should show liability accounts (Accounts Payable)
      if (isLastRow || hasCredit) {
        return this.accounts.filter(acc => 
          acc.accountType === 'Current Liability' ||
          acc.accountType === 'Long Term Liability' ||
          acc.accountType === 'Payroll Liability'
        );
      }
      // All other rows (debit/expense rows) should show expense accounts
      else {
        return this.accounts.filter(acc => 
          acc.accountType === 'Expense' || 
          acc.accountType === 'Other Expense' ||
          acc.accountType === 'Cost of Goods Sold' ||
          acc.accountType === 'Payroll Liability'
        );
      }
    },
    addRow() {
      this.rows.push({ account: '', memo: '', debit: '', credit: '' });
      this.validateEntries();
    },
    deleteRow(idx) {
      if (this.rows.length > 2) {
        this.rows.splice(idx, 1);
        this.validateEntries();
      }
    },
    validateEntries() {
      this.validationError = '';
      
      if (this.rows.length < 2) {
        this.validationError = 'At least two entries are required';
        return;
      }

      const unselectedAccounts = this.rows.filter(row => !row.account);
      if (unselectedAccounts.length > 0) {
        this.validationError = 'All accounts must be selected';
        return;
      }

      const invalidRows = this.rows.filter(row => {
        const debit = parseFloat(row.debit) || 0;
        const credit = parseFloat(row.credit) || 0;
        return (debit > 0 && credit > 0) || (debit === 0 && credit === 0);
      });
      
      if (invalidRows.length > 0) {
        this.validationError = 'Each entry must have either a debit OR credit amount, not both';
        return;
      }

      const hasDebit = this.rows.some(row => (parseFloat(row.debit) || 0) > 0);
      const hasCredit = this.rows.some(row => (parseFloat(row.credit) || 0) > 0);
      
      if (!hasDebit || !hasCredit) {
        this.validationError = 'At least one debit and one credit entry are required';
        return;
      }

      const debitTotal = this.debitTotal;
      const creditTotal = this.creditTotal;
      
      if (Math.abs(debitTotal - creditTotal) > 0.01) {
        this.isBalanced = false;
        this.validationError = `Totals must be equal. Debit: $${debitTotal.toFixed(2)}, Credit: $${creditTotal.toFixed(2)}`;
        return;
      }

      this.isBalanced = true;
    },
    handleDebitInput(row, value) {
      if (value && parseFloat(value) > 0) {
        row.credit = '';
      }
      this.validateEntries();
    },
    handleCreditInput(row, value) {
      if (value && parseFloat(value) > 0) {
        row.debit = '';
      }
      this.validateEntries();
    },
    async generateEntryNumber() {
      this.isLoadingEntryNo = true;
      this.entryNo = '';
      
      try {
        if (!this.companyId) {
          throw new Error('Company ID not available');
        }
        
        const response = await JournalEntryService.generateNextEntryNumber(this.companyId, 'BILL-JE');
        
        this.$nextTick(() => {
          this.entryNo = response.nextEntryNo;
        });
      } catch (error) {
        logger.error('Error generating entry number:', error);
        this.$nextTick(() => {
          this.entryNo = 'BILL-JE-001';
        });
      } finally {
        this.isLoadingEntryNo = false;
      }
    },
    async createJournalEntry(event) {
      const saveButton = event?.target;
      try {
        this.isLoading = true;
        if (saveButton) {
          saveButton.textContent = 'Creating...';
          saveButton.disabled = true;
        }
        await this.submitJournalEntryFromForm();
      } finally {
        this.isLoading = false;
        if (saveButton) {
          saveButton.textContent = 'Create';
          saveButton.disabled = false;
        }
      }
    },
    /**
     * POST journal entry from current form. If autoCreate+modal, emits `saved` without blocking toasts when skipped.
     * @returns {Promise<object|null>}
     */
    async submitJournalEntryFromForm() {
      if (!this.companyId) {
        if (this.autoCreate && this.isModal) {
          this.$emit('auto-create-skipped', { reason: 'Company ID not found.' });
          return null;
        }
        this.$toast?.error('Company ID not found. Please try again.') || alert('Company ID not found. Please try again.');
        return null;
      }
      if (!this.canSave) {
        if (this.autoCreate && this.isModal) {
          this.$emit('auto-create-skipped', {
            reason: this.validationError || 'Please fix all validation errors before creating.'
          });
          return null;
        }
        this.$toast?.error('Please fix all validation errors before saving.') || alert('Please fix all validation errors before saving.');
        return null;
      }

      const journalEntryData = {
        entryDate: this.date,
        comments: this.comments,
        billId: this.billData.billId || null,
        entries: this.rows.map(row => ({
          account: row.account,
          memo: row.memo,
          debit: parseFloat(row.debit) || 0,
          credit: parseFloat(row.credit) || 0
        }))
      };

      try {
        const response = await JournalEntryService.createJournalEntry(this.companyId, journalEntryData, 'BILL-JE');
        const je = response.journalEntry;

        if (this.autoCreate && this.isModal) {
          this.$emit('saved', je);
          return je;
        }

        this.$toast?.success('Bill journal entry created successfully!') ||
          alert(`Bill journal entry created successfully!\nEntry Number: ${je.entryNo}`);
        this.$emit('saved', je);
        return je;
      } catch (error) {
        logger.error('Error creating bill journal entry:', error);
        if (this.autoCreate && this.isModal) {
          const msg = error?.response?.data?.message || error?.message || 'Failed to create journal entry.';
          this.$emit('auto-create-failed', { error: msg });
          return null;
        }
        this.handleError(error);
        return null;
      }
    },
    async saveAndClose(event) {
      if (!this.canSave) {
        this.$toast?.error('Please fix all validation errors before saving.') || alert('Please fix all validation errors before saving.');
        return;
      }
      
      try {
        if (!this.companyId) {
          this.$toast?.error('Company ID not found. Please try again.') || alert('Company ID not found. Please try again.');
          return;
        }

        this.isLoading = true;
        const saveButton = event.target;
        saveButton.textContent = 'Saving...';
        saveButton.disabled = true;

        const journalEntryData = {
          entryDate: this.date,
          comments: this.comments,
          billId: this.billData.billId || null, // Include bill ID reference
          entries: this.rows.map(row => ({
            account: row.account,
            memo: row.memo,
            debit: parseFloat(row.debit) || 0,
            credit: parseFloat(row.credit) || 0
          }))
        };

        const response = await JournalEntryService.createJournalEntry(this.companyId, journalEntryData, 'BILL-JE');
        
        this.$toast?.success('Bill journal entry saved successfully!') || alert(`Bill journal entry saved successfully!\nEntry Number: ${response.journalEntry.entryNo}`);
        
        this.$router.push('/dashboard');
        
      } catch (error) {
        logger.error('Error saving bill journal entry:', error);
        this.handleError(error);
      } finally {
        this.isLoading = false;
        const saveButton = event.target;
        saveButton.textContent = 'Save and Close';
        saveButton.disabled = false;
      }
    },
    async saveAndNew(event) {
      if (!this.canSave) {
        this.$toast?.error('Please fix all validation errors before saving.') || alert('Please fix all validation errors before saving.');
        return;
      }
      
      try {
        if (!this.companyId) {
          this.$toast?.error('Company ID not found. Please try again.') || alert('Company ID not found. Please try again.');
          return;
        }

        this.isLoading = true;
        const saveButton = event.target;
        saveButton.textContent = 'Saving...';
        saveButton.disabled = true;

        const journalEntryData = {
          entryDate: this.date,
          comments: this.comments,
          billId: this.billData.billId || null, // Include bill ID reference
          entries: this.rows.map(row => ({
            account: row.account,
            memo: row.memo,
            debit: parseFloat(row.debit) || 0,
            credit: parseFloat(row.credit) || 0
          }))
        };

        const response = await JournalEntryService.createJournalEntry(this.companyId, journalEntryData, 'BILL-JE');
        
        this.$toast?.success('Bill journal entry saved successfully! Creating new entry...') || alert(`Bill journal entry saved successfully!\nEntry Number: ${response.journalEntry.entryNo}\n\nCreating new entry...`);
        
        this.resetForm();
        
      } catch (error) {
        logger.error('Error saving bill journal entry:', error);
        this.handleError(error);
      } finally {
        this.isLoading = false;
        const saveButton = event.target;
        saveButton.textContent = 'Save and New';
        saveButton.disabled = false;
      }
    },
    cancel() {
      if (this.isModal) {
        this.$emit('cancelled');
      } else {
        if (this.hasChanges) {
          if (confirm('You have unsaved changes. Are you sure you want to cancel? All changes will be lost.')) {
            this.$router.push('/dashboard');
          }
        } else {
          this.$router.push('/dashboard');
        }
      }
    },
    makeRecurring() {
      this.$toast?.info('Recurring bill journal entry feature coming soon!') || alert('Recurring bill journal entry feature coming soon!');
    },
    async resetForm() {
      // Set current date
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, '0');
      const dd = String(today.getDate()).padStart(2, '0');
      
      this.entryNo = '';
      this.date = `${yyyy}-${mm}-${dd}`;
      this.comments = '';
      this.rows = [
        { account: '', memo: '', debit: '', credit: '' },
        { account: '', memo: '', debit: '', credit: '' }
      ];
      this.validationError = '';
      this.isBalanced = false;
      
      await this.generateEntryNumber();
    },
    handleError(error) {
      logger.error('Error:', error);
      
      if (error.message.includes('Please log in')) {
        this.$toast?.error('Please log in to continue') || alert('Please log in to continue');
        this.$router.push('/login');
      } else if (error.message.includes('\n')) {
        const errors = error.message.split('\n');
        errors.forEach(err => {
          this.$toast?.error(err) || alert(err);
        });
      } else if (error.message.includes('Server error')) {
        this.$toast?.error('Unable to save bill journal entry. Please try again later.') || alert('Unable to save bill journal entry. Please try again later.');
      } else if (error.message.includes('No response')) {
        this.$toast?.error('Unable to connect to the server. Please check your internet connection.') || alert('Unable to connect to the server. Please check your internet connection.');
      } else {
        const errorMessage = error.response?.data?.message || error.message || 'An error occurred while saving the bill journal entry.';
        this.$toast?.error(errorMessage) || alert(`Error: ${errorMessage}`);
      }
    },
    async loadCompanyData() {
      try {
        if (!this.companyId) {
          return;
        }

        let companyData = this.$store.state.companies?.currentCompany;
        
        if (!companyData) {
          const response = await ApiService.getCompany(this.companyId);
          companyData = response.data;
          
          if (companyData) {
            this.$store.commit('companies/SET_CURRENT_COMPANY', companyData);
          }
        }

        if (this.companyId) {
          localStorage.setItem('currentCompanyId', this.companyId);
        }

      } catch (error) {
        logger.error('Error loading company data:', error);
        this.handleError(error);
      }
    },
    showHelpModal() {
      this.showHelp = true;
    },
    hideHelpModal() {
      this.showHelp = false;
    },
    // Helper method to convert ISO date (YYYY-MM-DD) to US format (MM/DD/YYYY)
    formatIsoToUs(isoDate) {
      if (!isoDate) return '';
      const date = new Date(isoDate);
      if (isNaN(date.getTime())) return '';
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      return `${month}/${day}/${year}`;
    },
    // Helper method to convert US format (MM/DD/YYYY) to ISO date (YYYY-MM-DD)
    parseUsToIso(usDate) {
      if (!usDate) return '';
      const parts = usDate.split('/');
      if (parts.length !== 3) return '';
      const month = parseInt(parts[0], 10);
      const day = parseInt(parts[1], 10);
      const year = parseInt(parts[2], 10);
      if (isNaN(month) || isNaN(day) || isNaN(year)) return '';
      return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    },
    // Auto-populate journal entry from bill data
    populateFromBill() {
      if (!this.autoPopulate || !this.billData || !this.billData.totalAmount) {
        return;
      }

      logger.log('Auto-populating bill journal entry from bill data:', this.billData);

      // Clear existing rows
      this.rows = [];

      const totalAmount = parseFloat(this.billData.totalAmount) || 0;
      const vendorName = this.billData.vendorName || this.billData.vendor?.vendorName || 'Vendor';
      const billNo = this.billData.billNo || '';

      // Get account records from bill (Itemize by Account / consultants / products)
      const accountRecords = this.billData.accountRecords || [];
      const consultingExpenseAccount = this.findConsultingExpenseAccount();

      // If consultant expense account differs from Consulting Expense, ask before posting
      if (!this.expenseAccountChoiceResolved) {
        const { mappedAccounts, message } = this.getNonDefaultMappedExpenseAccounts(
          accountRecords,
          consultingExpenseAccount
        );
        if (mappedAccounts.length > 0) {
          this.expenseAccountWarningMessage = message;
          this.showExpenseAccountWarning = true;
          if (this.autoCreate && this.isModal) {
            this.pendingAutoCreateAfterExpenseChoice = true;
          }
        }
      }

      // Find Accounts Payable (for credit) - prioritize account number 2100, then by name
      const accountsPayableAccount = this.accounts.find(acc => 
        acc.accountType === 'Current Liability' && 
        (acc.accountNumber === '2100' || acc.accountNumber === 2100)
      ) || this.accounts.find(acc => 
        acc.accountType === 'Current Liability' && 
        acc.accountName.toLowerCase().includes('payable')
      ) || this.accounts.find(acc => 
        acc.accountType === 'Current Liability' && 
        (acc.accountName.toLowerCase().includes('vendor') ||
         acc.accountName.toLowerCase().includes('bills'))
      ) || this.accounts.find(acc => 
        acc.accountType === 'Current Liability'
      );

      // If we have account records from the bill, create separate debit rows for each expense account
      if (accountRecords && accountRecords.length > 0) {
        accountRecords.forEach(record => {
          const amount = parseFloat(record.amount) || 0;
          if (amount <= 0) return;

          let accountId = record.accountId;
          if (this.isConsultantExpenseRecord(record)) {
            accountId = this.resolveConsultantExpenseAccountIdForRecord(
              record,
              consultingExpenseAccount
            );
          }

          if (accountId) {
            this.rows.push({
              account: accountId,
              memo: record.description || `Expense from ${vendorName}${billNo ? ' - Bill #' + billNo : ''}`,
              debit: amount,
              credit: ''
            });
          }
        });
      } else {
        // Fallback: Create one expense row with total amount (prefer Consulting Expense when available)
        const expenseAccount = consultingExpenseAccount || this.accounts.find(acc => 
          acc.accountType === 'Expense' &&
          !acc.accountName.toLowerCase().includes('tax')
        ) || this.accounts.find(acc => 
          acc.accountType === 'Other Expense' &&
          !acc.accountName.toLowerCase().includes('tax')
        ) || this.accounts.find(acc => 
          acc.accountType === 'Cost of Goods Sold'
        );

        if (expenseAccount) {
          this.rows.push({
            account: expenseAccount.id,
            memo: `Expense from ${vendorName}${billNo ? ' - Bill #' + billNo : ''}`,
            debit: totalAmount,
            credit: ''
          });
        } else {
          this.rows.push({
            account: '',
            memo: `Expense from ${vendorName}${billNo ? ' - Bill #' + billNo : ''}`,
            debit: totalAmount,
            credit: ''
          });
        }
      }

      // Credit: Accounts Payable (single row with total amount)
      if (accountsPayableAccount) {
        this.rows.push({
          account: accountsPayableAccount.id,
          memo: `Amount owed to ${vendorName}${billNo ? ' - Bill #' + billNo : ''}`,
          debit: '',
          credit: totalAmount
        });
      } else {
        // Add empty row if no payable account found
        this.rows.push({
          account: '',
          memo: `Amount owed to ${vendorName}${billNo ? ' - Bill #' + billNo : ''}`,
          debit: '',
          credit: totalAmount
        });
      }

      // Ensure minimum 2 rows
      if (this.rows.length < 2) {
        this.rows.push(
          { account: '', memo: '', debit: '', credit: '' },
          { account: '', memo: '', debit: '', credit: '' }
        );
      }

      // Validate the populated entries
      this.$nextTick(() => {
        this.validateEntries();
      });

      // Show info about auto-population
      const hasExpenseAccounts = accountRecords && accountRecords.length > 0;
      if (hasExpenseAccounts && accountsPayableAccount) {
        logger.log(`Successfully auto-populated bill journal entry with ${accountRecords.length} expense account(s)`);
      } else if (accountsPayableAccount) {
        logger.log('Successfully auto-populated bill journal entry with fallback expense account');
      } else {
        const missingAccounts = [];
        if (!hasExpenseAccounts) missingAccounts.push('Expense account records');
        if (!accountsPayableAccount) missingAccounts.push('Accounts Payable account');
        logger.warn(`Auto-population incomplete. Missing: ${missingAccounts.join(', ')}`);
      }
    },
    findConsultingExpenseAccount() {
      let consultingExpenseAccount = this.accounts.find((acc) =>
        String(acc.accountName || '').toLowerCase() === 'consulting expense' &&
        (acc.accountType === 'Expense' || acc.accountType === 'Cost of Goods Sold' || acc.accountType === 'Other Expense')
      );
      if (!consultingExpenseAccount) {
        consultingExpenseAccount = this.accounts.find((acc) =>
          (acc.accountType === 'Expense' || acc.accountType === 'Cost of Goods Sold' || acc.accountType === 'Other Expense') &&
          String(acc.accountName || '').toLowerCase().includes('consulting expense')
        );
      }
      return consultingExpenseAccount || null;
    },
    isDefaultConsultingExpenseAccount(account) {
      if (!account) return false;
      return String(account.accountName || '').toLowerCase().includes('consulting expense');
    },
    isConsultantExpenseRecord(record) {
      if (!record) return false;
      if (String(record.itemType || '').toLowerCase() === 'consultant') return true;
      const desc = String(record.description || '').toLowerCase();
      return desc.startsWith('consultant expense');
    },
    /**
     * Collect non-default mapped expense accounts from consultant bill lines.
     */
    getNonDefaultMappedExpenseAccounts(accountRecords, consultingExpenseAccount) {
      const byId = new Map();
      (accountRecords || []).forEach((record) => {
        if (!this.isConsultantExpenseRecord(record)) return;
        const amount = parseFloat(record.amount) || 0;
        if (amount <= 0 || !record.accountId) return;
        const account = this.accounts.find((acc) => String(acc.id) === String(record.accountId));
        if (!account) return;
        if (consultingExpenseAccount && String(account.id) === String(consultingExpenseAccount.id)) return;
        if (this.isDefaultConsultingExpenseAccount(account)) return;
        if (!byId.has(String(account.id))) {
          byId.set(String(account.id), account);
        }
      });

      const mappedAccounts = Array.from(byId.values());
      let message = '';
      if (mappedAccounts.length === 1) {
        const a = mappedAccounts[0];
        message =
          `Expense account is mapped to "${a.accountName}"` +
          (a.accountNumber ? ` (${a.accountNumber})` : '') +
          '. Do you want to proceed with this account?\n\n' +
          'Yes = use the mapped expense account.\n' +
          'No = continue with Consulting Expense.';
      } else if (mappedAccounts.length > 1) {
        const list = mappedAccounts
          .map((a) => `• ${a.accountName}${a.accountNumber ? ` (${a.accountNumber})` : ''}`)
          .join('\n');
        message =
          `Expense account is mapped to the following account(s):\n${list}\n\n` +
          'Do you want to proceed with these mapped accounts?\n\n' +
          'Yes = use the mapped expense account(s).\n' +
          'No = continue with Consulting Expense.';
      }
      return { mappedAccounts, message };
    },
    /**
     * Resolve which expense account id to use for a consultant debit line.
     * Default / No → Consulting Expense. Yes → consultant mapped account.
     */
    resolveConsultantExpenseAccountIdForRecord(record, consultingExpenseAccount) {
      const useMapped =
        this.expenseAccountChoiceResolved && this.useConsultantExpenseAccount === true;
      if (useMapped && record.accountId) {
        return record.accountId;
      }
      if (consultingExpenseAccount) {
        return consultingExpenseAccount.id;
      }
      return record.accountId || null;
    },
    async confirmMappedExpenseAccount() {
      this.showExpenseAccountWarning = false;
      this.useConsultantExpenseAccount = true;
      this.expenseAccountChoiceResolved = true;
      this.populateFromBill();
      if (this.pendingAutoCreateAfterExpenseChoice) {
        this.pendingAutoCreateAfterExpenseChoice = false;
        await this.$nextTick();
        this.validateEntries();
        if (!this.canSave) {
          const reason = this.validationError || 'Journal entry could not be built from bill data.';
          this.$emit('auto-create-skipped', { reason });
          return;
        }
        await this.submitJournalEntryFromForm();
      }
    },
    async declineMappedExpenseAccount() {
      this.showExpenseAccountWarning = false;
      this.useConsultantExpenseAccount = false;
      this.expenseAccountChoiceResolved = true;
      this.populateFromBill();
      if (this.pendingAutoCreateAfterExpenseChoice) {
        this.pendingAutoCreateAfterExpenseChoice = false;
        await this.$nextTick();
        this.validateEntries();
        if (!this.canSave) {
          const reason = this.validationError || 'Journal entry could not be built from bill data.';
          this.$emit('auto-create-skipped', { reason });
          return;
        }
        await this.submitJournalEntryFromForm();
      }
    }
  },
  beforeRouteLeave(to, from, next) {
    if (this.hasChanges) {
      if (confirm('You have unsaved changes. Are you sure you want to leave? All changes will be lost.')) {
        next();
      } else {
        next(false);
      }
    } else {
      next();
    }
  },
  async created() {
    try {
      await this.loadCompanyData();
      
      if (this.companyId) {
        const res = await ApiService.getCompanyAccounts(this.companyId);
        this.accounts = res.data.accounts || res.data || [];
        await this.generateEntryNumber();
        
        // Set date from billData if provided (convert from US format to ISO)
        if (this.billData && this.billData.billDate) {
          // Check if date is in US format (MM/DD/YYYY) or ISO format (YYYY-MM-DD)
          if (this.billData.billDate.includes('/')) {
            // US format - convert to ISO
            this.date = this.parseUsToIso(this.billData.billDate);
          } else {
            // Already ISO format
            this.date = this.billData.billDate;
          }
        }
        
        // Set comments from billData if provided
        if (this.billData && this.billData.billNo) {
          this.comments = `Bill journal entry for Bill #${this.billData.billNo}`;
        }
        
        // Auto-populate journal entry from bill data
        if (this.autoPopulate && this.billData && this.billData.totalAmount) {
          this.populateFromBill();
        }

        // Pause auto-create while waiting for expense-account mapping confirmation
        if (this.autoCreate && this.isModal && this.companyId) {
          if (this.showExpenseAccountWarning || this.pendingAutoCreateAfterExpenseChoice) {
            logger.log('Bill journal auto-create paused for expense account confirmation');
            return;
          }
          await this.$nextTick();
          this.validateEntries();
          if (!this.canSave) {
            const reason = this.validationError || 'Journal entry could not be built from bill data.';
            logger.warn('Bill journal auto-create skipped:', reason);
            this.$emit('auto-create-skipped', { reason });
            return;
          }
          await this.submitJournalEntryFromForm();
        }
      } else {
        if (this.autoCreate && this.isModal) {
          this.$emit('auto-create-skipped', { reason: 'Unable to load company data.' });
        } else {
          this.$toast?.error('Unable to load company data. Please try again.') || alert('Unable to load company data. Please try again.');
        }
      }
    } catch (e) {
      logger.error('Error loading accounts:', e);
      this.accounts = [];
      if (this.autoCreate && this.isModal) {
        const msg = e?.response?.data?.message || e?.message || 'Failed to load accounts.';
        this.$emit('auto-create-failed', { error: msg });
      } else {
        this.handleError(e);
      }
    }
  },
  mounted() {
    this.validateEntries();
  },
  watch: {
    // Watch for changes in bill data to auto-populate
    billData: {
      handler(newData) {
        if (
          newData &&
          this.autoPopulate &&
          newData.totalAmount &&
          this.accounts.length > 0 &&
          !this.showExpenseAccountWarning &&
          !this.pendingAutoCreateAfterExpenseChoice
        ) {
          this.populateFromBill();
        }
      },
      deep: true
    }
  }
};
</script>
